import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import { LeadsIcon, EmailIcon, PhoneIcon } from "../assets/icons/icons";
import PricingSection from "../components/PricingSection";
import { trackGoal } from "../utils/datafast";
import useFocusTrap from "../hooks/useFocusTrap";

const STATUSES = ["new", "seen", "archived"];
const PAGE_SIZES = [10, 20, 50, 100];

const buildPaginationItems = (current, total) => {
  if (total <= 1) return [];
  const items = [];
  const pushRange = (start, end) => {
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
  };

  if (total <= 7) {
    pushRange(1, total);
    return items;
  }

  items.push(1);
  if (current <= 4) {
    pushRange(2, 5);
    items.push("dots");
    items.push(total);
    return items;
  }

  if (current >= total - 3) {
    items.push("dots");
    pushRange(Math.max(2, total - 4), total - 1);
    items.push(total);
    return items;
  }

  items.push("dots");
  pushRange(current - 1, current + 1);
  items.push("dots");
  items.push(total);
  return items;
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionTier, setSubscriptionTier] = useState("free");
  const [subscriptionStatus, setSubscriptionStatus] = useState("inactive");
  const [showPricingModal, setShowPricingModal] = useState(false);
  const pricingModalRef = React.useRef(null);

  const loadLeads = async () => {
    setLoading(true);
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) {
      setLoading(false);
      return;
    }
    const [{ data: profileRow }, { data, error: leadErr }] = await Promise.all([
      supabase.from("profiles").select("subscription_tier,subscription_status").eq("id", userId).maybeSingle(),
      supabase.from("leads_masked").select("*").eq("owner_id", userId).order("created_at", { ascending: false }),
    ]);
    if (profileRow) {
      setSubscriptionTier(profileRow.subscription_tier || "free");
      setSubscriptionStatus(profileRow.subscription_status || "inactive");
    }
    if (leadErr) {
      setLeads([]);
    } else {
      setLeads(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const updateStatus = async (id, status) => {
    const target = leads.find((lead) => lead.id === id) || null;
    const previousStatus = String(target?.status || "new").toLowerCase();
    const { error: updErr } = await supabase
      .from("leads")
      .update({ status }, { returning: "minimal" })
      .eq("id", id);
    if (updErr) return;
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    const nextStatus = String(status || "").toLowerCase();
    const action = previousStatus === "archived" && nextStatus === "seen" ? "restored" : nextStatus || "seen";
    const leadType = String(target?.lead_type || "").toLowerCase() ||
      ((target?.inquiry_details || target?.notes) ? "inquiry" : "share_contact");
    trackGoal("leads_acted_on", { action, lead_type: leadType });
  };

  const effectiveTier = useMemo(() => {
    const tier = String(subscriptionTier || "").toLowerCase();
    const status = String(subscriptionStatus || "").toLowerCase();
    if (tier === "pro" || tier === "elite") return tier;
    if (status === "pro" || status === "elite") return status;
    if (status === "active" || status === "trialing") return "pro";
    return "free";
  }, [subscriptionTier, subscriptionStatus]);

  const isElite = effectiveTier === "elite";
  const isPro = effectiveTier === "pro";
  const isPaid = isPro || isElite;

  const filteredLeads = useMemo(() => {
    const term = isPaid ? search.trim().toLowerCase() : "";
    return leads.filter((l) => {
      const matchesStatus = statusFilter === "all" ? true : (l.status || "").toLowerCase() === statusFilter;
      if (!matchesStatus) return false;
      const type = (l.lead_type || "").toLowerCase() || ((l.inquiry_details || l.notes) ? "inquiry" : "share_contact");
      const matchesType = typeFilter === "all" ? true : type === typeFilter;
      if (!matchesType) return false;
      if (!term) return true;
      return (
        (l.name || "").toLowerCase().includes(term) ||
        (l.email || "").toLowerCase().includes(term) ||
        (l.phone || "").toLowerCase().includes(term) ||
        (isElite ? (l.viewer_name || "").toLowerCase().includes(term) : false) ||
        (isElite ? (l.viewer_email || "").toLowerCase().includes(term) : false) ||
        (isElite ? (l.viewer_phone || "").toLowerCase().includes(term) : false) ||
        (l.inquiry_details || l.notes || "").toLowerCase().includes(term)
      );
    });
  }, [leads, statusFilter, typeFilter, search, isPaid, isElite]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter, search, isPaid, isElite, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );
  const paginatedLeads = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLeads.slice(start, start + pageSize);
  }, [currentPage, filteredLeads, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const counts = useMemo(() => {
    const out = { new: 0, seen: 0, archived: 0 };
    leads.forEach((l) => {
      const s = (l.status || "new").toLowerCase();
      if (out[s] !== undefined) out[s] += 1;
    });
    return out;
  }, [leads]);

  const viewSummary = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - 6);

    const summary = {
      profileToday: 0,
      profileWeek: 0,
      licenseToday: 0,
      licenseWeek: 0,
    };

    leads.forEach((lead) => {
      let viewType = String(lead.view_target_type || "").toLowerCase();
      if (!viewType) {
        const leadType = String(lead.lead_type || "").toLowerCase();
        if (leadType === "profile_view") viewType = "profile";
        if (leadType === "license_view") viewType = "license";
      }
      if (viewType !== "profile" && viewType !== "license") return;
      const createdAt = new Date(lead.created_at);
      if (Number.isNaN(createdAt.getTime())) return;
      const isToday = createdAt >= startOfToday;
      const isWeek = createdAt >= startOfWeek;
      if (viewType === "profile") {
        if (isWeek) summary.profileWeek += 1;
        if (isToday) summary.profileToday += 1;
        return;
      }
      if (isWeek) summary.licenseWeek += 1;
      if (isToday) summary.licenseToday += 1;
    });

    return summary;
  }, [leads]);

  const getLeadType = (lead) => {
    const type = String(lead.lead_type || "").toLowerCase();
    if (type) return type;
    if (lead.inquiry_details || lead.notes) return "inquiry";
    return "share_contact";
  };

  const getLeadSummary = (lead) => {
    const viewType = String(lead.view_target_type || "").toLowerCase();
    if (viewType === "license") return "Someone viewed your license";
    if (viewType === "profile") return "Someone viewed your profile";
    return "Someone viewed your profile";
  };

  const getLeadDisplayName = (lead) => {
    if (!isPaid) return getLeadSummary(lead);
    return lead.name || (isElite ? lead.viewer_name : "") || "Unknown visitor";
  };

  const getLeadEmail = (lead) => {
    if (!isPaid) return "";
    return lead.email || (isElite ? lead.viewer_email : "") || "";
  };

  const getLeadPhone = (lead) => {
    if (!isPaid) return "";
    return lead.phone || (isElite ? lead.viewer_phone : "") || "";
  };

  const formatPhone = (val) => {
    if (!val) return "-";
    const digits = val.replace(/\D/g, "");
    if (digits.length < 10) return val;
    const area = digits.slice(0, 3);
    const mid = digits.slice(3, 6);
    const last = digits.slice(6, 10);
    return `(${area}) ${mid}-${last}`;
  };

  useFocusTrap({
    isOpen: showPricingModal,
    containerRef: pricingModalRef,
    onClose: () => setShowPricingModal(false),
    lockScroll: true,
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="eyebrow">Leads</p>
          <h1 data-page-heading="true">Your Leads</h1>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h4>Views Summary</h4>
        </div>
        <div className="stat-grid">
          <div className="stat-card blue">
            <div className="stat-value">{viewSummary.profileToday}</div>
            <div className="stat-label">Profile Views Today</div>
          </div>
          <div className="stat-card green">
            <div className="stat-value">{viewSummary.profileWeek}</div>
            <div className="stat-label">Profile Views Last 7 Days</div>
          </div>
          <div className="stat-card amber">
            <div className="stat-value">{viewSummary.licenseToday}</div>
            <div className="stat-label">License Views Today</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-value">{viewSummary.licenseWeek}</div>
            <div className="stat-label">License Views Last 7 Days</div>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-header">
          <h4>Filters</h4>
        </div>
        <div className="form-row triple">
          <div>
            <label htmlFor="leads-filter-status">Status</label>
            <select id="leads-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="leads-filter-type">Type</label>
            <select id="leads-filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="share_contact">Share contact</option>
              <option value="inquiry">Inquiry</option>
            </select>
          </div>
          <div>
            <label htmlFor="leads-filter-search">Search</label>
            <input
              id="leads-filter-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isPaid ? "Search name, email, phone" : "Upgrade to search by name, email, phone"}
              disabled={!isPaid}
            />
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h4>Leads</h4>
          <div className="badges" style={{ gap: 6 }}>
            <span className="badge" style={{ background: "#fff4e5", color: "#c26007" }}>
              {counts.new} New
            </span>
            <span className="badge" style={{ background: "#e7f7ff", color: "#0b74b3" }}>
              {counts.seen} Seen
            </span>
            <span className="badge muted">{counts.archived} Archived</span>
          </div>
        </div>
        {loading && <p className="muted">Loading...</p>}
        {!loading && filteredLeads.length === 0 && <p className="muted">No leads match your filters.</p>}
        <div className="license-list lead-list" style={{ gap: 12 }}>
          {paginatedLeads.map((lead) => {
            const leadType = getLeadType(lead);
            const leadTypeLabel = isPaid
              ? leadType.replace("_", " ")
              : String(lead.view_target_type || "profile").toLowerCase() === "license"
              ? "license view"
              : "profile view";
            const leadName = getLeadDisplayName(lead);
            const leadEmail = getLeadEmail(lead);
            const leadPhone = getLeadPhone(lead);
            const emailDisplay = leadEmail || "--";
            const phoneDisplay = leadPhone ? formatPhone(leadPhone) : "--";
            const receivedAt = lead.created_at ? new Date(lead.created_at).toLocaleString() : "n/a";
            const showInquiry = isPaid && (lead.inquiry_details || lead.notes);
            const viewLabel = isPaid ? (lead.view_target_label || "") : "";
            return (
              <div key={lead.id} className="license-card admin-card lead-card">
                <div className="lead-card-header">
                  <div className="lead-name-block">
                    <LeadsIcon className="card-line-icon" />
                    <h4>{leadName}</h4>
                  </div>
                  <span className="badge muted" style={{ textTransform: "capitalize" }}>
                    {leadTypeLabel}
                  </span>
                  <span
                    className={`badge ${
                      lead.status === "new"
                        ? "amber"
                        : lead.status === "archived"
                        ? "muted"
                        : "success"
                    }`}
                    style={
                      lead.status === "new"
                        ? { background: "#fff4e5", color: "#c26007" }
                        : lead.status === "seen"
                        ? { background: "#e7f7ff", color: "#0b74b3" }
                        : {}
                    }
                  >
                    {lead.status || "new"}
                  </span>
                </div>

                <div className="lead-details">
                  {isPaid ? (
                    <>
                      <div className="lead-line">
                        <EmailIcon className="card-line-icon accent" />
                        <span className="lead-value">{emailDisplay}</span>
                      </div>
                      <div className="lead-line">
                        <PhoneIcon className="card-line-icon accent" />
                        <span className="lead-value">{phoneDisplay}</span>
                      </div>
                      {showInquiry ? (
                        <p className="muted small" style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
                          <strong>Inquiry Details:</strong> {lead.inquiry_details || lead.notes}
                        </p>
                      ) : null}
                      {viewLabel ? (
                        <p className="muted small">
                          <strong>Viewed:</strong> {viewLabel}
                        </p>
                      ) : null}
                      {isElite ? (
                        <>
                          <p className="muted small"><strong>Device:</strong> {lead.device_type || "unknown"}</p>
                          <p className="muted small"><strong>Source:</strong> {lead.source_slug || "n/a"}</p>
                        </>
                      ) : null}
                      <p className="muted small"><strong>Received:</strong> {receivedAt}</p>
                    </>
                  ) : (
                    <p className="muted small"><strong>Received:</strong> {receivedAt}</p>
                  )}
                </div>

                <div className="lead-actions">
                  {lead.status !== "seen" && lead.status !== "archived" && (
                    <button className="ghost" type="button" onClick={() => updateStatus(lead.id, "seen")}>
                      Mark seen
                    </button>
                  )}
                  {lead.status !== "archived" && (
                    <button className="ghost" type="button" onClick={() => updateStatus(lead.id, "archived")}>
                      Archive
                    </button>
                  )}
                  {lead.status === "archived" && (
                    <button className="ghost" type="button" onClick={() => updateStatus(lead.id, "seen")}>
                      Restore to seen
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {!loading && filteredLeads.length > 0 && totalPages > 1 && (
          <div className="pagination-bar" style={{ marginTop: 18 }}>
            <div className="pagination-pill">
              <button
                className="pagination-arrow"
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage <= 1}
                aria-label="Previous page"
              >
                {"<"}
              </button>
              {paginationItems.map((item, idx) =>
                item === "dots" ? (
                  <span key={`dots-${idx}`} className="pagination-dots">...</span>
                ) : (
                  <button
                    key={item}
                    className={`pagination-page ${item === currentPage ? "active" : ""}`}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    aria-label={`Page ${item}`}
                  >
                    {item}
                  </button>
                )
              )}
              <button
                className="pagination-arrow"
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                {">"}
              </button>
            </div>
            <div className="pagination-size">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Results per page"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {!isElite && (
        <div className="panel">
          <div className="panel-header">
            <h4>{isPro ? "Own Your Category" : "You Have Interested Leads"}</h4>
          </div>
          <p className="muted" style={{ marginTop: 6 }}>
            {isPro
              ? "Get top placement, premium visibility, and priority exposure where clients are already searching."
              : "People are viewing your profile and licenses right now. Unlock lead details to see who they are and follow up while interest is fresh."}
          </p>
          <div className="button-row" style={{ marginTop: 12 }}>
            <button
              className="primary"
              type="button"
              onClick={() => {
                trackGoal("upsell_interacted", {
                  upsell_id: isPro ? "leads_own_category" : "leads_unlock",
                  action: isPro ? "claim_elite_placement" : "unlock_leads",
                });
                setShowPricingModal(true);
              }}
            >
              {isPro ? "Claim Elite Placement" : "Unlock Leads"}
            </button>
          </div>
        </div>
      )}

      {showPricingModal && (
        <div className="modal-backdrop" style={{ zIndex: 210 }} onClick={() => setShowPricingModal(false)}>
          <div
            ref={pricingModalRef}
            className="modal pricing-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="leads-pricing-modal-title"
            style={{ maxWidth: 1200, maxHeight: "80vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pricing-modal-card">
              <h3 id="leads-pricing-modal-title" className="sr-only">Pricing plans</h3>
              <button
                className="icon-btn pricing-modal-close"
                type="button"
                onClick={() => setShowPricingModal(false)}
                aria-label="Close"
              >
                x
              </button>
              <PricingSection
                id="pricing-modal-leads"
                disableFreeCta
                freeCtaLabel="Current Plan"
                currentTier={subscriptionTier}
                currentStatus={subscriptionStatus}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


